/**
 * Every room with a landing page at /ai-interior-design-generator/<slug>, in
 * the order the room lists show them (most searched first). Client-safe and
 * small: each room's questions and ideas stay on the server (getStaticProps).
 *
 * `name` is the room's English name on the pages (and its translation key);
 * `tool` is the room type the AI Interior Designer is opened with when it
 * differs (the value the tool sends to the API).
 */
export type InteriorRoomGroup = 'home' | 'outdoor' | 'business';

export interface InteriorRoom {
  slug: string;
  name: string;
  group: InteriorRoomGroup;
  tool?: string;
  /** Titles mention remodeling ("ai kitchen remodel" is searched). */
  remodel?: boolean;
}

export const INTERIOR_ROOMS: InteriorRoom[] = [
  { slug: 'living-room', name: 'Living Room', group: 'home' },
  { slug: 'bedroom', name: 'Bedroom', group: 'home' },
  { slug: 'kitchen', name: 'Kitchen', group: 'home', remodel: true },
  { slug: 'bathroom', name: 'Bathroom', group: 'home', remodel: true },
  { slug: 'home-office', name: 'Home Office', group: 'home' },
  { slug: 'kids-room', name: 'Kids Room', group: 'home' },
  { slug: 'dining-room', name: 'Dining Room', group: 'home' },
  {
    slug: 'walk-in-closet',
    name: 'Walk-in Closet',
    group: 'home',
    tool: 'Walk in Closet',
  },
  { slug: 'nursery', name: 'Nursery', group: 'home' },
  { slug: 'basement', name: 'Basement', group: 'home', remodel: true },
  { slug: 'gaming-room', name: 'Gaming Room', group: 'home' },
  { slug: 'laundry-room', name: 'Laundry Room', group: 'home' },
  {
    slug: 'apartment',
    name: 'Apartment',
    group: 'home',
    tool: 'Apartment / Studio Apartment',
  },
  { slug: 'attic', name: 'Attic', group: 'home', remodel: true },
  { slug: 'entryway', name: 'Entryway', group: 'home' },
  { slug: 'pantry', name: 'Pantry', group: 'home' },
  { slug: 'home-gym', name: 'Home Gym', group: 'home' },
  { slug: 'man-cave', name: 'Man Cave', group: 'home' },
  { slug: 'home-theater', name: 'Home Theater', group: 'home' },
  { slug: 'mudroom', name: 'Mudroom', group: 'home' },
  { slug: 'sunroom', name: 'Sunroom', group: 'home' },
  { slug: 'backyard', name: 'Backyard', group: 'outdoor' },
  { slug: 'front-yard', name: 'Front Yard', group: 'outdoor' },
  {
    slug: 'outdoor-garden',
    name: 'Garden',
    group: 'outdoor',
    tool: 'Outdoor Garden',
  },
  {
    slug: 'outdoor-patio',
    name: 'Patio',
    group: 'outdoor',
    tool: 'Outdoor Patio',
  },
  { slug: 'deck', name: 'Deck', group: 'outdoor' },
  {
    slug: 'outdoor-pool-area',
    name: 'Pool Area',
    group: 'outdoor',
    tool: 'Outdoor Pool Area',
  },
  { slug: 'balcony', name: 'Balcony', group: 'outdoor' },
  { slug: 'house-exterior', name: 'House Exterior', group: 'outdoor' },
  { slug: 'outdoor-kitchen', name: 'Outdoor Kitchen', group: 'outdoor' },
  {
    slug: 'covered-porch',
    name: 'Porch',
    group: 'outdoor',
    tool: 'Covered Porch',
  },
  { slug: 'rooftop-terrace', name: 'Rooftop Terrace', group: 'outdoor' },
  { slug: 'swimming-pool', name: 'Swimming Pool', group: 'outdoor' },
  { slug: 'office', name: 'Office', group: 'business' },
  { slug: 'restaurant', name: 'Restaurant', group: 'business' },
  { slug: 'coffee-shop', name: 'Coffee Shop', group: 'business' },
  { slug: 'classroom', name: 'Classroom', group: 'business' },
  { slug: 'gym', name: 'Gym', group: 'business' },
  { slug: 'beauty-salon', name: 'Beauty Salon', group: 'business' },
  { slug: 'hotel-lobby', name: 'Hotel Lobby', group: 'business' },
  { slug: 'clothing-store', name: 'Clothing Store', group: 'business' },
  { slug: 'bar', name: 'Bar', group: 'business' },
  { slug: 'barbershop', name: 'Barbershop', group: 'business' },
  { slug: 'hotel-room', name: 'Hotel Room', group: 'business' },
  { slug: 'dental-office', name: 'Dental Office', group: 'business' },
  {
    slug: 'daycare',
    name: 'Daycare',
    group: 'business',
    tool: 'Daycare / Childcare Center',
  },
  { slug: 'bakery', name: 'Bakery', group: 'business' },
  { slug: 'church', name: 'Church', group: 'business' },
  { slug: 'coworking-space', name: 'Coworking Space', group: 'business' },
  { slug: 'study-room', name: 'Study Room', group: 'home' },
  { slug: 'meeting-room', name: 'Meeting Room', group: 'business' },
  { slug: 'podcast-studio', name: 'Podcast Studio', group: 'business' },
  { slug: 'workshop', name: 'Workshop', group: 'business' },
  { slug: 'toilet', name: 'Toilet', group: 'home' },
  { slug: 'hotel-bathroom', name: 'Hotel Bathroom', group: 'business' },
  { slug: 'exhibition-space', name: 'Exhibition Space', group: 'business' },
  { slug: 'onsen', name: 'Onsen', group: 'home' },
  { slug: 'drop-zone', name: 'Drop Zone', group: 'home' },
  {
    slug: 'outdoor-garage',
    name: 'Detached Garage',
    group: 'outdoor',
    tool: 'Outdoor Garage',
  },
  { slug: 'garage', name: 'Garage', group: 'home', remodel: true },
  { slug: 'garage-gym', name: 'Garage Gym', group: 'home' },
  { slug: 'carport', name: 'Carport', group: 'outdoor' },
  { slug: 'wine-cellar', name: 'Wine Cellar', group: 'home' },
  { slug: 'home-library', name: 'Home Library', group: 'home' },
  { slug: 'greenhouse', name: 'Greenhouse', group: 'outdoor' },
  { slug: 'dressing-room', name: 'Dressing Room', group: 'home' },
  { slug: 'powder-room', name: 'Powder Room', group: 'home' },
  { slug: 'utility-room', name: 'Utility Room', group: 'home' },
  { slug: 'billiards-room', name: 'Billiards Room', group: 'home' },
  { slug: 'trophy-room', name: 'Trophy Room', group: 'home' },
  { slug: 'foyer', name: 'Foyer', group: 'home' },
  { slug: 'loft', name: 'Loft', group: 'home' },
  { slug: 'she-shed', name: 'She Shed', group: 'outdoor' },
  { slug: 'ensuite-bathroom', name: 'Ensuite Bathroom', group: 'home' },
  { slug: 'breakfast-nook', name: 'Breakfast Nook', group: 'home' },
  { slug: 'tool-shed', name: 'Tool Shed', group: 'outdoor' },
  { slug: 'cabana', name: 'Cabana', group: 'outdoor' },
  { slug: 'yoga-studio', name: 'Yoga Studio', group: 'business' },
  { slug: 'storage-room', name: 'Storage Room', group: 'home' },
  { slug: 'sewing-room', name: 'Sewing Room', group: 'home' },
  { slug: 'waiting-room', name: 'Waiting Room', group: 'business' },
  { slug: 'reception-area', name: 'Reception Area', group: 'business' },
  { slug: 'hall', name: 'Hallway', group: 'home', tool: 'Hall' },
  { slug: 'meditation-room', name: 'Meditation Room', group: 'home' },
  {
    slug: 'content-creator-studio',
    name: 'Creator Studio',
    group: 'home',
    tool: 'Content Creator Studio',
  },
  {
    slug: 'adu-suite',
    name: 'ADU Suite',
    group: 'home',
    tool: 'ADU / Mother-in-Law Suite',
  },
  {
    slug: 'flex-space',
    name: 'Flex Room',
    group: 'home',
    tool: 'Multi-Functional / Flex Space',
  },
  { slug: 'reading-nook', name: 'Reading Nook', group: 'home' },
  {
    slug: 'wellness-room',
    name: 'Spa Room',
    group: 'home',
    tool: 'Wellness / Spa Room',
  },
  {
    slug: 'music-room',
    name: 'Music Room',
    group: 'home',
    tool: 'Music Listening Room',
  },
  { slug: 'pet-room', name: 'Pet Room', group: 'home' },
  {
    slug: 'craft-room',
    name: 'Craft Room',
    group: 'home',
    tool: 'Craft Room / Creative Studio',
  },
  { slug: 'recording-studio', name: 'Recording Studio', group: 'business' },
  {
    slug: 'plant-room',
    name: 'Plant Room',
    group: 'home',
    tool: 'Plant Room / Indoor Garden',
  },
  {
    slug: 'safe-room',
    name: 'Safe Room',
    group: 'home',
    tool: 'Safe Room / Panic Room',
  },
  {
    slug: 'spa-retreat',
    name: 'Day Spa',
    group: 'business',
    tool: 'Spa Retreat',
  },
  {
    slug: 'tiny-house',
    name: 'Tiny House',
    group: 'home',
    tool: 'Tiny House / Micro Home',
  },
  { slug: 'tattoo-studio', name: 'Tattoo Studio', group: 'business' },
  { slug: 'pharmacy', name: 'Pharmacy', group: 'business' },
  {
    slug: 'pilates-studio',
    name: 'Pilates Studio',
    group: 'business',
    tool: 'Pilates / Barre Studio',
  },
  {
    slug: 'campervan',
    name: 'Campervan',
    group: 'home',
    tool: 'Van Interior / Campervan',
  },
];

export const INTERIOR_ROOM_GROUPS: { id: InteriorRoomGroup; label: string }[] =
  [
    { id: 'home', label: 'Home' },
    { id: 'outdoor', label: 'Outdoor' },
    { id: 'business', label: 'Business' },
  ];

export const findInteriorRoom = (slug: string | null | undefined) =>
  INTERIOR_ROOMS.find((room) => room.slug === slug) ?? null;

/** Room type value the AI Interior Designer gets for this room. */
export const toolRoomType = (room: InteriorRoom) => room.tool ?? room.name;
